// Sales > Lead Finder filters (PD-16825).
//
// The Lead Finder list reads workd_mission_control.lm_lead_location, which has
// no TypeORM entity, so these filters are read by key off the request's
// `filters` payload by the crm-api lead-finder service, which builds bound SQL
// (apps/crm-api/src/modules/lead-finder/lead-finder-sql.ts) — not through
// applyFiltersToQueryBuilder. Option ids are the server's codes.

import {
	LEAD_FINDER_CLASS_OF_TRADE_LABELS,
	LEAD_FINDER_CLASSES_OF_TRADE,
	LEAD_FINDER_ISSUED_WITHIN_DAYS,
	LEAD_FINDER_OWNERSHIP_LABELS,
	LEAD_FINDER_OWNERSHIPS,
} from '@supply-mover-2-0/shared';
import { createKeywordFilter } from './factories';
import type { FilterConfig, FilterRegistry } from './types';

export const LEAD_FINDER_LIST_KEY = 'lead_finder';
const LEAD_FINDER_LIST_KEYS: ReadonlyArray<string> = [LEAD_FINDER_LIST_KEY];

const custom = { objectTable: 'lm_lead_location', joinType: 'custom' as const };

const textFilter = (label: string, placeholder: string, queryParamName: string): FilterConfig => ({
	...createKeywordFilter({ label, placeholder, objectTable: 'lm_lead_location', listKeys: [...LEAD_FINDER_LIST_KEYS] }),
	queryParamName,
	queryMapping: custom,
});

export const leadFinderFilters: FilterRegistry = {
	leadFinderKeywords: textFilter('Search', 'Pharmacy or legal name...', 'leadFinderKeywords'),

	leadFinderLeadStatus: {
		type: 'radio',
		label: 'Lead status',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: { id: 'id' as keyof unknown, primary: 'label' as keyof unknown },
		queryParamName: 'leadFinderLeadStatus',
		multiple: false,
		// 'any', not 'all': FilterService drops a radio value of 'all' before
		// sending, which would turn "All" back into the server default (New).
		options: [
			{ id: 'new', label: 'New leads only' },
			{ id: 'possible', label: 'Possible existing clients' },
			{ id: 'any', label: 'All' },
			// The Dismissed tab locks the filter to this value; every other value
			// leaves dismissed locations out.
			{ id: 'dismissed', label: 'Dismissed (not a fit)' },
		],
		defaultValue: { id: 'new', label: 'New leads only' },
		queryMapping: custom,
		format: 'single',
		valueExtractor: { property: 'id' },
		listKeys: [...LEAD_FINDER_LIST_KEYS],
	},

	leadFinderClassOfTrade: {
		type: 'multi-select',
		label: 'Class of trade',
		placeholder: 'Select class of trade...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: { id: 'id' as keyof unknown, primary: 'label' as keyof unknown },
		options: LEAD_FINDER_CLASSES_OF_TRADE.map((value) => ({ id: value, label: LEAD_FINDER_CLASS_OF_TRADE_LABELS[value] })),
		queryParamName: 'leadFinderClassOfTrade',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: custom,
		format: 'array',
		listKeys: [...LEAD_FINDER_LIST_KEYS],
	},

	leadFinderOwnership: {
		type: 'multi-select',
		label: 'Independent / chain',
		placeholder: 'Select ownership...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: { id: 'id' as keyof unknown, primary: 'label' as keyof unknown },
		options: LEAD_FINDER_OWNERSHIPS.map((value) => ({ id: value, label: LEAD_FINDER_OWNERSHIP_LABELS[value] })),
		queryParamName: 'leadFinderOwnership',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: custom,
		format: 'array',
		listKeys: [...LEAD_FINDER_LIST_KEYS],
	},

	leadFinderState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: { id: 'id' as keyof unknown, primary: 'label' as keyof unknown },
		apiEndpoint: '/lead-finder/filter-options/states',
		queryParamName: 'leadFinderState',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: custom,
		format: 'array',
		listKeys: [...LEAD_FINDER_LIST_KEYS],
	},

	leadFinderCity: textFilter('City', 'City...', 'leadFinderCity'),

	leadFinderZip: textFilter('ZIP', 'ZIP or ZIP prefix...', 'leadFinderZip'),

	leadFinderIssuedWithin: {
		type: 'single-select',
		label: 'Issued within',
		placeholder: 'Any time',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: LEAD_FINDER_ISSUED_WITHIN_DAYS.map((days) => ({ id: String(days), label: `${days} days` })),
		displayFields: { id: 'id' as keyof unknown, primary: 'label' as keyof unknown },
		queryParamName: 'leadFinderIssuedWithin',
		multiple: false,
		queryMapping: custom,
		format: 'single',
		valueExtractor: { property: 'id' },
		listKeys: [...LEAD_FINDER_LIST_KEYS],
	},
};
